import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import PersonIcon from "@mui/icons-material/Person";
import SatelliteAltIcon from "@mui/icons-material/SatelliteAlt";
import {
  Box,
  Button,
  CircularProgress,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { COLORS } from "app/theme";
import { useTranslation } from "common/locales";
import { queryKeys } from "common/queryKeys";
import type {
  AnimalGroup,
  Field,
  ManualDMInputResponse,
  MoveEvent,
  StockClass,
} from "model";
import {
  EventType,
  getAllFieldsForAnimalGroupIdFromArray,
  getDemandFromArray,
  useAnimalGroupMoveEvents,
  useCreateReading,
  useDeleteReading,
  useEventsForFields,
  useFields,
  useUpdateReading,
} from "model";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { RotationAnalysis } from "./rotationPlanner";
import { analyzeRotation } from "./rotationPlanner";

// ─── Types ───────────────────────────────────────────────────────────────────

type EventStatus = "accepted" | "proposed";
type EventOrigin = "user" | "machine";

interface GridCell {
  fieldGuid: string;
  fieldName: string;
  rotation: number;
  moveEvent?: MoveEvent;
  reading?: ManualDMInputResponse;
  duplicates?: ManualDMInputResponse[]; // extra readings for same time window
  status: EventStatus | null; // null = empty cell
  origin: EventOrigin; // user input or machine (satellite) detected
  dateIn?: string;
  dateOut?: string | null;
  flagged?: boolean; // unrealistically long event
}

// ─── Constants ───────────────────────────────────────────────────────────────

const MAX_SENSIBLE_GRAZING_DAYS = 21;

const STATUS_COLORS: Record<
  EventStatus,
  { bg: string; bgLight: string; border: string; text: string }
> = {
  accepted: {
    bg: COLORS.dahlia[300],
    bgLight: COLORS.dahlia[100],
    border: COLORS.dahlia[500],
    text: COLORS.dahlia[900],
  },
  proposed: {
    bg: COLORS.bellflower[300],
    bgLight: COLORS.bellflower[100],
    border: COLORS.bellflower[500],
    text: COLORS.bellflower[900],
  },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getSeasonYears(
  moveEvents: MoveEvent[],
  readingsByField: Record<string, ManualDMInputResponse[]>
): number[] {
  const years = new Set<number>();

  for (const ev of moveEvents) {
    years.add(new Date(ev.dateTimeIn).getFullYear());
    if (ev.dateTimeOut) years.add(new Date(ev.dateTimeOut).getFullYear());
  }

  for (const readings of Object.values(readingsByField)) {
    for (const r of readings) {
      const ev = r.manualDryMatterInput.event;
      if (
        ev.eventType !== EventType.GrazingEvent &&
        ev.eventType !== EventType.PartialGrazingEvent
      )
        continue;
      if (ev.dateTimeIn) years.add(new Date(ev.dateTimeIn).getFullYear());
    }
  }

  if (years.size === 0) years.add(new Date().getFullYear());
  return Array.from(years).sort((a, b) => b - a);
}

function getDaysBetween(start: string, end: string | null): number {
  const endDate = end ? new Date(end) : new Date();
  const startDate = new Date(start);
  return Math.max(
    1,
    Math.round((endDate.getTime() - startDate.getTime()) / 86400000)
  );
}

interface SeasonInfo {
  seasonStart: Date | null;
  seasonEnd: Date | null;
  isCurrentSeason: boolean;
  warnings: string[];
}

/**
 * Get season date range from move events (first event start → last event end).
 * Detects nonsensical events: cross-year spans, events outside the season year.
 */
function getSeasonDateRange(
  moveEvents: MoveEvent[],
  readingsByField: Record<string, ManualDMInputResponse[]>,
  seasonYear: number,
  t: (key: string, vars?: Record<string, unknown>) => string
): SeasonInfo {
  const isCurrentSeason = seasonYear === new Date().getFullYear();
  const warnings: string[] = [];

  let earliest: Date | null = null;
  let latest: Date | null = null;
  let hasOpenEvent = false;

  // Process moveEvents
  const filteredMoves = moveEvents.filter(
    (ev) => new Date(ev.dateTimeIn).getFullYear() === seasonYear
  );
  for (const ev of filteredMoves) {
    const inDate = new Date(ev.dateTimeIn);
    if (!earliest || inDate < earliest) earliest = inDate;

    if (ev.dateTimeOut) {
      const outDate = new Date(ev.dateTimeOut);
      if (!latest || outDate > latest) latest = outDate;
      if (outDate.getFullYear() !== seasonYear) {
        warnings.push(
          t("rotationGrid.warningCrossYear", { field: ev.fieldName ?? "" })
        );
      }
    } else {
      hasOpenEvent = true;
    }
  }

  // Process readings
  for (const readings of Object.values(readingsByField)) {
    for (const r of readings) {
      const ev = r.manualDryMatterInput.event;
      if (
        ev.eventType !== EventType.GrazingEvent &&
        ev.eventType !== EventType.PartialGrazingEvent
      )
        continue;
      if (!ev.dateTimeIn) continue;
      if (new Date(ev.dateTimeIn).getFullYear() !== seasonYear) continue;

      const inDate = new Date(ev.dateTimeIn);
      if (!earliest || inDate < earliest) earliest = inDate;

      if (ev.dateTimeOut) {
        const outDate = new Date(ev.dateTimeOut);
        if (!latest || outDate > latest) latest = outDate;
      } else {
        hasOpenEvent = true;
      }
    }
  }

  if (!earliest)
    return { seasonStart: null, seasonEnd: null, isCurrentSeason, warnings };

  if (isCurrentSeason) {
    // Current season is always ongoing — don't show an end date
    latest = null;
  } else {
    // Past seasons: use the latest dateTimeOut we found
    // Cap at Dec 31 if it bleeds into the next year
    if (latest && latest.getFullYear() > seasonYear) {
      latest = new Date(seasonYear, 11, 31);
    }
    // If no dateTimeOut found at all, use the latest dateTimeIn as a fallback
    if (!latest) {
      let latestIn: Date | null = null;
      for (const ev of filteredMoves) {
        const d = new Date(ev.dateTimeIn);
        if (!latestIn || d > latestIn) latestIn = d;
      }
      for (const readings of Object.values(readingsByField)) {
        for (const r of readings) {
          const evr = r.manualDryMatterInput.event;
          if (!evr.dateTimeIn) continue;
          if (new Date(evr.dateTimeIn).getFullYear() !== seasonYear) continue;
          const d = new Date(evr.dateTimeIn);
          if (!latestIn || d > latestIn) latestIn = d;
        }
      }
      latest = latestIn;
    }
  }

  return {
    seasonStart: earliest,
    seasonEnd: latest,
    isCurrentSeason,
    warnings,
  };
}

/**
 * Group move events into rotation cycles.
 * A new rotation starts when:
 * - We encounter a field already visited in the current rotation AND
 * - We've visited at least 1/3 of the total unique fields (avoids splitting
 *   on same-day or back-to-back moves to the same field)
 *
 * After grouping, empty rotations are removed and gaps are compacted.
 */
function groupIntoRotations(
  moveEvents: MoveEvent[],
  seasonYear: number
): Map<number, MoveEvent[]> {
  const filtered = moveEvents.filter((ev) => {
    const year = new Date(ev.dateTimeIn).getFullYear();
    return year === seasonYear;
  });

  // Count unique fields to set a sensible threshold
  const uniqueFields = new Set(
    filtered.map((ev) => ev.fieldGuid).filter(Boolean)
  );
  const minBeforeSplit = Math.max(2, Math.ceil(uniqueFields.size / 3));

  const rotations = new Map<number, MoveEvent[]>();
  let currentRotation = 1;
  const visitedInCurrentRotation = new Set<string>();

  for (const ev of filtered) {
    const key = ev.fieldGuid ?? "__shed__";
    if (
      visitedInCurrentRotation.has(key) &&
      visitedInCurrentRotation.size >= minBeforeSplit
    ) {
      currentRotation++;
      visitedInCurrentRotation.clear();
    }
    visitedInCurrentRotation.add(key);
    if (!rotations.has(currentRotation)) {
      rotations.set(currentRotation, []);
    }
    const bucket = rotations.get(currentRotation);
    if (bucket) bucket.push(ev);
  }

  // Compact: remove empty rotations and re-number
  const compacted = new Map<number, MoveEvent[]>();
  let idx = 1;
  for (const [, events] of rotations) {
    if (events.length > 0) {
      compacted.set(idx++, events);
    }
  }

  return compacted;
}

/**
 * Sort fields by:
 * 1. Chronological order of first grazing (from moveEvents + readings)
 * 2. For ungrazed fields: highest dryMatterNow first (likely grazed next)
 * 3. Tie-break by geographic latitude (north → south)
 */
function sortFieldsByRotationOrder(
  fields: Field[],
  moveEvents: MoveEvent[],
  readingsByField: Record<string, ManualDMInputResponse[]>,
  seasonYear: number
): Field[] {
  // Store earliest dateTimeIn per field from moveEvents
  const firstGrazedDate = new Map<string, number>();

  for (const ev of moveEvents) {
    if (!ev.fieldGuid) continue;
    if (new Date(ev.dateTimeIn).getFullYear() !== seasonYear) continue;
    const time = new Date(ev.dateTimeIn).getTime();
    const existing = firstGrazedDate.get(ev.fieldGuid);
    if (existing == null || time < existing) {
      firstGrazedDate.set(ev.fieldGuid, time);
    }
  }

  // Also consider readings (they may have events not in moveEvents)
  for (const [fieldGuid, readings] of Object.entries(readingsByField)) {
    for (const r of readings) {
      const ev = r.manualDryMatterInput.event;
      if (
        ev.eventType !== EventType.GrazingEvent &&
        ev.eventType !== EventType.PartialGrazingEvent
      )
        continue;
      if (!ev.dateTimeIn) continue;
      if (new Date(ev.dateTimeIn).getFullYear() !== seasonYear) continue;
      const time = new Date(ev.dateTimeIn).getTime();
      const existing = firstGrazedDate.get(fieldGuid);
      if (existing == null || time < existing) {
        firstGrazedDate.set(fieldGuid, time);
      }
    }
  }

  return [...fields].sort((a, b) => {
    const aTime = firstGrazedDate.get(a.guid);
    const bTime = firstGrazedDate.get(b.guid);

    // Grazed fields first, in chronological order of first grazing
    if (aTime != null && bTime != null) return aTime - bTime;
    if (aTime != null) return -1;
    if (bTime != null) return 1;

    // Ungrazed: highest dryMatterNow first (likely next to be grazed)
    const dmDiff = (b.dryMatterNow ?? 0) - (a.dryMatterNow ?? 0);
    if (Math.abs(dmDiff) > 0.0001) return dmDiff;

    // Tie-break: latitude (north to south)
    const aLat = getFieldLatitude(a);
    const bLat = getFieldLatitude(b);
    return bLat - aLat;
  });
}

function getFieldLatitude(field: Field): number {
  try {
    const geom = field.geom;
    if (!geom) return 0;
    const coords =
      geom.geometry?.type === "Polygon"
        ? (geom.geometry as { coordinates: number[][][] }).coordinates[0]
        : geom.geometry?.type === "MultiPolygon"
        ? (geom.geometry as { coordinates: number[][][][] }).coordinates[0][0]
        : null;
    if (!coords || coords.length === 0) return 0;
    return coords.reduce((sum, c) => sum + c[1], 0) / coords.length;
  } catch {
    return 0;
  }
}

function findMatchingReading(
  moveEvent: MoveEvent,
  readings: ManualDMInputResponse[]
): ManualDMInputResponse | undefined {
  if (!moveEvent.fieldGuid) return undefined;
  const moveIn = new Date(moveEvent.dateTimeIn).getTime();

  return readings.find((r) => {
    const item = r.manualDryMatterInput;
    if (item.field !== moveEvent.fieldGuid) return false;
    if (!item.event.dateTimeIn) return false;
    const readingIn = new Date(item.event.dateTimeIn).getTime();
    return Math.abs(readingIn - moveIn) < 86400000;
  });
}

function getCellStatus(
  reading?: ManualDMInputResponse,
  moveEvent?: MoveEvent
): EventStatus | null {
  const vs =
    reading?.manualDryMatterInput.verificationStatus ??
    moveEvent?.verificationStatus;
  if (!vs) return null;
  return vs === "Proposed" ? "proposed" : "accepted";
}

function getCellOrigin(
  reading?: ManualDMInputResponse,
  moveEvent?: MoveEvent
): EventOrigin {
  const kind = reading?.manualDryMatterInput.kind ?? moveEvent?.kind;
  return kind === "machine" ? "machine" : "user";
}

// ─── Rotation Column Builder ─────────────────────────────────────────────────

interface MissingField {
  guid: string;
  name: string;
}

interface RotationColumn {
  rotation: number;
  totalDays: number;
  events: GridCell[];
  missingFields: MissingField[];
}

function buildRotationColumns(
  assignedFields: Field[],
  moveEvents: MoveEvent[],
  readingsByField: Record<string, ManualDMInputResponse[]>,
  seasonYear: number
): RotationColumn[] {
  const rotations = groupIntoRotations(moveEvents, seasonYear);

  const columns: RotationColumn[] = [];

  for (const [rotNum, events] of rotations) {
    const cells: GridCell[] = [];
    let totalDays = 0;
    const visitedFieldGuids = new Set<string>();

    for (const ev of events) {
      if (!ev.fieldGuid) continue;
      visitedFieldGuids.add(ev.fieldGuid);

      // Find matching reading for this event
      const fieldReadings = (readingsByField[ev.fieldGuid] || []).filter(
        (r) => {
          const re = r.manualDryMatterInput.event;
          return (
            (re.eventType === EventType.GrazingEvent ||
              re.eventType === EventType.PartialGrazingEvent) &&
            re.dateTimeIn &&
            new Date(re.dateTimeIn).getFullYear() === seasonYear
          );
        }
      );
      const reading = findMatchingReading(ev, fieldReadings);

      const hasEnd = !!ev.dateTimeOut;
      const days = hasEnd
        ? getDaysBetween(ev.dateTimeIn, ev.dateTimeOut)
        : null;
      if (days != null) totalDays += days;

      cells.push({
        fieldGuid: ev.fieldGuid,
        fieldName: ev.fieldName ?? "—",
        rotation: rotNum,
        moveEvent: ev,
        reading,
        status: getCellStatus(reading, ev),
        origin: getCellOrigin(reading, ev),
        dateIn: ev.dateTimeIn,
        dateOut: ev.dateTimeOut,
      });
    }

    // Missing fields: assigned fields not visited in this rotation
    const missingFields = assignedFields
      .filter((f) => !visitedFieldGuids.has(f.guid))
      .map((f) => ({ guid: f.guid, name: f.name }));

    columns.push({
      rotation: rotNum,
      totalDays: Math.round(totalDays * 10) / 10,
      events: cells,
      missingFields,
    });
  }

  return columns;
}

// ─── Legacy Grid Builder (kept for ReviewPanel compatibility) ─────────────────

function buildGrid(
  fields: Field[],
  moveEvents: MoveEvent[],
  readingsByField: Record<string, ManualDMInputResponse[]>,
  seasonYear: number
): { grid: GridCell[][]; rotationCount: number } {
  const rotations = groupIntoRotations(moveEvents, seasonYear);
  const rotationCount = Math.max(rotations.size, 3);

  const moveByFieldAndRotation = new Map<string, Map<number, MoveEvent>>();
  for (const [rotNum, events] of rotations) {
    for (const ev of events) {
      if (!ev.fieldGuid) continue;
      if (!moveByFieldAndRotation.has(ev.fieldGuid)) {
        moveByFieldAndRotation.set(ev.fieldGuid, new Map());
      }
      const fieldMap = moveByFieldAndRotation.get(ev.fieldGuid);
      if (fieldMap) fieldMap.set(rotNum, ev);
    }
  }

  const grid: GridCell[][] = fields.map((field) => {
    const fieldMoves = moveByFieldAndRotation.get(field.guid);
    const fieldReadings = (readingsByField[field.guid] || []).filter((r) => {
      const ev = r.manualDryMatterInput.event;
      return (
        (ev.eventType === EventType.GrazingEvent ||
          ev.eventType === EventType.PartialGrazingEvent) &&
        ev.dateTimeIn &&
        new Date(ev.dateTimeIn).getFullYear() === seasonYear
      );
    });

    // Collect readings not matched to any moveEvent, sorted by dateTimeIn
    const unmatchedReadings = fieldReadings
      .filter((r) => {
        const rIn = r.manualDryMatterInput.event.dateTimeIn;
        if (!rIn) return false;
        const rTime = new Date(rIn).getTime();
        if (!fieldMoves) return true;
        for (const [, me] of fieldMoves) {
          if (Math.abs(new Date(me.dateTimeIn).getTime() - rTime) < 86400000)
            return false;
        }
        return true;
      })
      .sort(
        (a, b) =>
          new Date(a.manualDryMatterInput.event.dateTimeIn ?? 0).getTime() -
          new Date(b.manualDryMatterInput.event.dateTimeIn ?? 0).getTime()
      );

    // Group unmatched readings by similar dateTimeIn (within 24h = duplicates)
    const dedupedGroups: {
      primary: ManualDMInputResponse;
      duplicates: ManualDMInputResponse[];
    }[] = [];
    for (const r of unmatchedReadings) {
      const rTime = new Date(
        r.manualDryMatterInput.event.dateTimeIn ?? 0
      ).getTime();
      const existingGroup = dedupedGroups.find((g) => {
        const gTime = new Date(
          g.primary.manualDryMatterInput.event.dateTimeIn ?? 0
        ).getTime();
        return Math.abs(rTime - gTime) < 86400000;
      });
      if (existingGroup) {
        existingGroup.duplicates.push(r);
      } else {
        dedupedGroups.push({ primary: r, duplicates: [] });
      }
    }

    let groupIdx = 0;

    const row: GridCell[] = [];
    for (let rot = 1; rot <= rotationCount; rot++) {
      const moveEvent = fieldMoves?.get(rot);

      if (moveEvent) {
        const reading = findMatchingReading(moveEvent, fieldReadings);
        const hasEnd = !!moveEvent.dateTimeOut;
        const days = hasEnd
          ? getDaysBetween(moveEvent.dateTimeIn, moveEvent.dateTimeOut)
          : null;

        row.push({
          fieldGuid: field.guid,
          fieldName: field.name,
          rotation: rot,
          moveEvent,
          reading,
          status: getCellStatus(reading, moveEvent),
          origin: getCellOrigin(reading, moveEvent),
          dateIn: moveEvent.dateTimeIn,
          dateOut: moveEvent.dateTimeOut,
          flagged: hasEnd && days != null && days > MAX_SENSIBLE_GRAZING_DAYS,
        });
      } else if (groupIdx < dedupedGroups.length) {
        const group = dedupedGroups[groupIdx++];
        const reading = group.primary;
        const ev = reading.manualDryMatterInput.event;
        const dateIn = ev.dateTimeIn ?? "";
        const dateOut = ev.dateTimeOut ?? null;
        const days = getDaysBetween(dateIn, dateOut);

        row.push({
          fieldGuid: field.guid,
          fieldName: field.name,
          rotation: rot,
          reading,
          duplicates:
            group.duplicates.length > 0 ? group.duplicates : undefined,
          status: getCellStatus(reading),
          origin: getCellOrigin(reading),
          dateIn,
          dateOut,
          flagged: days > MAX_SENSIBLE_GRAZING_DAYS,
        });
      } else {
        row.push({
          fieldGuid: field.guid,
          fieldName: field.name,
          rotation: rot,
          status: null,
          origin: "user",
        });
      }
    }
    return row;
  });

  return { grid, rotationCount };
}

/**
 * Suggest a date range for a new event in an empty cell,
 * based on surrounding events in the same rotation.
 */
const MIN_SUGGESTED_DAYS = 2;

function suggestEventDates(
  grid: GridCell[][],
  fieldIndex: number,
  rotationIndex: number
): { suggestedIn: Date | null; suggestedOut: Date | null } {
  // Look for the nearest filled cell above in the same rotation
  let prevOut: Date | null = null;
  for (let fi = fieldIndex - 1; fi >= 0; fi--) {
    const cell = grid[fi][rotationIndex];
    if (cell.status === null) continue;
    if (cell.dateOut) {
      prevOut = new Date(cell.dateOut);
    } else if (cell.dateIn) {
      // If no dateOut, estimate: dateIn + 3 days
      prevOut = new Date(new Date(cell.dateIn).getTime() + 3 * 86400000);
    }
    break;
  }

  // Look for the nearest filled cell below in the same rotation
  let nextIn: Date | null = null;
  for (let fi = fieldIndex + 1; fi < grid.length; fi++) {
    const cell = grid[fi][rotationIndex];
    if (cell.status === null) continue;
    if (cell.dateIn) {
      nextIn = new Date(cell.dateIn);
    }
    break;
  }

  let suggestedIn: Date | null = null;
  let suggestedOut: Date | null = null;

  if (prevOut && nextIn) {
    suggestedIn = prevOut;
    suggestedOut = nextIn;
  } else if (prevOut) {
    suggestedIn = prevOut;
    suggestedOut = new Date(prevOut.getTime() + 3 * 86400000);
  } else if (nextIn) {
    suggestedIn = new Date(nextIn.getTime() - 3 * 86400000);
    suggestedOut = nextIn;
  }

  // Ensure minimum gap between suggested dates
  if (suggestedIn && suggestedOut) {
    const gap = suggestedOut.getTime() - suggestedIn.getTime();
    if (gap < MIN_SUGGESTED_DAYS * 86400000) {
      suggestedOut = new Date(
        suggestedIn.getTime() + MIN_SUGGESTED_DAYS * 86400000
      );
    }
  }

  return { suggestedIn, suggestedOut };
}

// ─── Cell Component ──────────────────────────────────────────────────────────

function GrazingEventCell({
  cell,
  onAccept,
  onSaveEdit,
  onAddEvent,
  onDeleteReading,
  isAddingEvent,
  isSaving,
  grid,
  fieldIndex,
  rotationIndex,
}: {
  cell: GridCell;
  onAccept: (cell: GridCell) => void;
  onSaveEdit: (cell: GridCell, dateIn: string, dateOut: string) => void;
  onAddEvent: (
    cell: GridCell,
    suggestion: { suggestedIn: Date | null; suggestedOut: Date | null }
  ) => void;
  onDeleteReading: (readingGuid: string, fieldGuid: string) => void;
  isAddingEvent: boolean;
  isSaving: boolean;
  grid: GridCell[][];
  fieldIndex: number;
  rotationIndex: number;
}) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [justAccepted, setJustAccepted] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [localDateIn, setLocalDateIn] = useState("");
  const [localDateOut, setLocalDateOut] = useState("");

  const effectiveStatus = justAccepted ? "accepted" : cell.status;
  const isProposed = effectiveStatus === "proposed";
  const colors = effectiveStatus ? STATUS_COLORS[effectiveStatus] : null;

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setIsEditing(false);
    if (cell.dateIn) {
      setLocalDateIn(new Date(cell.dateIn).toISOString().slice(0, 10));
      setLocalDateOut(
        cell.dateOut ? new Date(cell.dateOut).toISOString().slice(0, 10) : ""
      );
    }
  };

  const handleAccept = () => {
    setJustAccepted(true);
    onAccept(cell);
    setAnchorEl(null);
  };

  const OriginIcon = cell.origin === "machine" ? SatelliteAltIcon : PersonIcon;

  const sessionDays = useMemo(() => {
    if (!cell.dateIn) return null;
    if (!cell.dateOut) return null; // ongoing — don't show inflated day count
    return getDaysBetween(cell.dateIn, cell.dateOut);
  }, [cell.dateIn, cell.dateOut]);

  const preCover = cell.reading?.manualDryMatterInput.event.preEventCover;
  const postCover = cell.reading?.manualDryMatterInput.event.postEventCover;
  const showDryMatter = !isProposed;

  const isEmpty = cell.status === null;

  // For empty cells, compute suggestion on demand
  const suggestion = useMemo(() => {
    if (!isEmpty) return { suggestedIn: null, suggestedOut: null };
    return suggestEventDates(grid, fieldIndex, rotationIndex);
  }, [isEmpty, grid, fieldIndex, rotationIndex]);

  const hasSuggestion = suggestion.suggestedIn !== null;

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          width: "100%",
          minHeight: 44,
          borderRadius: 1.5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
          backgroundColor: colors ? colors.bg : "transparent",
          border: colors
            ? `1.5px solid ${colors.border}`
            : "1.5px dashed #E0E0E0",
          position: "relative",
          px: 0.75,
          py: cell.status ? 0.5 : 0,
          "&:hover": isEmpty
            ? {
                borderColor: "#BDBDBD",
                backgroundColor: "#FAFAFA",
                "& .add-hint": { opacity: 1 },
              }
            : {
                filter: "brightness(0.95)",
                transform: "scale(1.02)",
              },
          ...(justAccepted && {
            animation: "acceptPulse 0.6s ease-out",
            "@keyframes acceptPulse": {
              "0%": { transform: "scale(1)", boxShadow: "none" },
              "50%": {
                transform: "scale(1.08)",
                boxShadow: "0 0 12px rgba(76, 175, 80, 0.5)",
              },
              "100%": { transform: "scale(1)", boxShadow: "none" },
            },
          }),
        }}
      >
        {/* Duplicate badge */}
        {cell.duplicates && cell.duplicates.length > 0 && (
          <Tooltip
            title={t("rotationGrid.duplicateTooltip", {
              count: cell.duplicates.length + 1,
            })}
            placement="top"
          >
            <Box
              sx={{
                position: "absolute",
                top: -4,
                left: -4,
                width: 18,
                height: 18,
                borderRadius: "50%",
                backgroundColor: "#D32F2F",
                color: "white",
                fontSize: "0.6rem",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1,
              }}
            >
              {cell.duplicates.length + 1}
            </Box>
          </Tooltip>
        )}

        {cell.status !== null ? (
          <>
            {/* Field name + date row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                width: "100%",
              }}
            >
              <OriginIcon
                sx={{
                  fontSize: 12,
                  color: colors?.text,
                  opacity: 0.7,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.65rem",
                  color: colors?.text,
                  fontWeight: 700,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  flex: 1,
                }}
              >
                {cell.fieldName}
              </Typography>
              {cell.dateIn && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.6rem",
                    color: colors?.text,
                    fontWeight: 500,
                    lineHeight: 1.2,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    opacity: 0.8,
                  }}
                >
                  {new Date(cell.dateIn).toLocaleDateString(undefined, {
                    day: "numeric",
                    month: "short",
                  })}
                </Typography>
              )}
            </Box>
            {/* Session length */}
            {sessionDays != null && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.55rem",
                  color: colors?.text,
                  fontWeight: 400,
                  opacity: 0.8,
                  lineHeight: 1.2,
                }}
              >
                {sessionDays}d
              </Typography>
            )}
            {/* Pre/post cover */}
            {showDryMatter && (preCover != null || postCover != null) && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.5rem",
                  color: colors?.text,
                  opacity: 0.7,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                }}
              >
                {preCover != null ? Math.round(preCover) : "—"}
                {" → "}
                {postCover != null ? Math.round(postCover) : "—"}
              </Typography>
            )}
          </>
        ) : (
          /* Empty cell: show add hint on hover */
          <AddCircleOutlineIcon
            className="add-hint"
            sx={{
              fontSize: 18,
              color: "#BDBDBD",
              opacity: 0,
              transition: "opacity 0.2s",
            }}
          />
        )}
      </Box>

      {/* Popover for filled cells */}
      {cell.status !== null && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
          slotProps={{
            paper: {
              sx: { borderRadius: 2, p: 2, minWidth: 220, maxWidth: 300 },
            },
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {cell.fieldName}
          </Typography>
          {cell.dateIn && (
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block" }}
            >
              {new Date(cell.dateIn).toLocaleDateString()}
              {cell.dateOut
                ? ` → ${new Date(cell.dateOut).toLocaleDateString()}`
                : ` → ${t("rotationTimeline.current")}`}
              {sessionDays != null && ` (${sessionDays}d)`}
            </Typography>
          )}

          {/* Pre/post cover if available */}
          {showDryMatter &&
            (cell.reading?.manualDryMatterInput.event.preEventCover != null ||
              cell.reading?.manualDryMatterInput.event.postEventCover !=
                null) && (
              <Typography
                variant="caption"
                sx={{ display: "block", color: "text.secondary", mt: 0.5 }}
              >
                {cell.reading?.manualDryMatterInput.event.preEventCover !=
                  null &&
                  `${t("rotationGrid.preCover")}: ${Math.round(
                    cell.reading.manualDryMatterInput.event.preEventCover
                  )} kg/ha`}
                {cell.reading?.manualDryMatterInput.event.preEventCover !=
                  null &&
                  cell.reading?.manualDryMatterInput.event.postEventCover !=
                    null &&
                  " · "}
                {cell.reading?.manualDryMatterInput.event.postEventCover !=
                  null &&
                  `${t("rotationGrid.postCover")}: ${Math.round(
                    cell.reading.manualDryMatterInput.event.postEventCover
                  )} kg/ha`}
              </Typography>
            )}

          {/* Duplicate cleanup */}
          {cell.duplicates && cell.duplicates.length > 0 && (
            <Box
              sx={{
                mt: 1,
                px: 1,
                py: 0.75,
                borderRadius: 1,
                backgroundColor: "#FFEBEE",
                border: "1px solid #EF9A9A",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "#C62828",
                  fontWeight: 500,
                  display: "block",
                  mb: 0.5,
                }}
              >
                {t("rotationGrid.duplicateWarning", {
                  count: cell.duplicates.length + 1,
                })}
              </Typography>
              <Button
                size="small"
                variant="contained"
                fullWidth
                onClick={() => {
                  for (const dup of cell.duplicates ?? []) {
                    onDeleteReading(dup.guid, cell.fieldGuid);
                  }
                  setAnchorEl(null);
                }}
                sx={{
                  textTransform: "none",
                  backgroundColor: "#C62828",
                  "&:hover": { backgroundColor: "#B71C1C" },
                  fontSize: "0.75rem",
                }}
              >
                {t("rotationGrid.removeDuplicates", {
                  count: cell.duplicates.length,
                })}
              </Button>
            </Box>
          )}

          {/* Accept button for proposed events */}
          {isProposed && !isEditing && (
            <Button
              size="small"
              variant="contained"
              color="success"
              fullWidth
              startIcon={<CheckCircleIcon />}
              onClick={handleAccept}
              sx={{ textTransform: "none", mt: 1.5 }}
            >
              {t("rotationGrid.accept")}
            </Button>
          )}

          {/* Inline edit form */}
          {isEditing ? (
            <Box
              sx={{
                mt: 1.5,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <TextField
                label={t("rotationGrid.editDateIn")}
                type="date"
                size="small"
                value={localDateIn}
                onChange={(e) => setLocalDateIn(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
              />
              <TextField
                label={t("rotationGrid.editDateOut")}
                type="date"
                size="small"
                value={localDateOut}
                onChange={(e) => setLocalDateOut(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled={isSaving || !localDateIn}
                  onClick={() => {
                    onSaveEdit(cell, localDateIn, localDateOut);
                    setAnchorEl(null);
                  }}
                  sx={{ textTransform: "none", flex: 1 }}
                >
                  {t("settings.button.save")}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setIsEditing(false)}
                  sx={{ textTransform: "none" }}
                >
                  {t("settings.button.close")}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setIsEditing(true)}
                sx={{ textTransform: "none", flex: 1 }}
              >
                {t("rotationGrid.edit")}
              </Button>
              {cell.reading && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<CloseIcon />}
                  onClick={() => {
                    onDeleteReading(cell.reading?.guid ?? "", cell.fieldGuid);
                    setAnchorEl(null);
                  }}
                  sx={{ textTransform: "none" }}
                >
                  {t("rotationGrid.delete")}
                </Button>
              )}
            </Box>
          )}
        </Popover>
      )}

      {/* Popover for empty cells — add event */}
      {isEmpty && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
          slotProps={{
            paper: {
              sx: { borderRadius: 2, p: 2, minWidth: 220, maxWidth: 280 },
            },
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {cell.fieldName}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block", mb: 1.5 }}
          >
            {t("rotationGrid.addEventHint")}
          </Typography>

          {hasSuggestion && (
            <Box
              sx={{
                mb: 1.5,
                px: 1,
                py: 0.75,
                borderRadius: 1,
                backgroundColor: "#F3E5F5",
                border: "1px solid #CE93D8",
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "#6A1B9A", fontWeight: 500, display: "block" }}
              >
                {t("rotationGrid.suggestedDates")}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#6A1B9A", display: "block" }}
              >
                {suggestion.suggestedIn?.toLocaleDateString(undefined, {
                  day: "numeric",
                  month: "short",
                })}
                {" → "}
                {suggestion.suggestedOut?.toLocaleDateString(undefined, {
                  day: "numeric",
                  month: "short",
                })}
              </Typography>
            </Box>
          )}

          <Button
            size="small"
            variant="contained"
            color="primary"
            fullWidth
            disabled={isAddingEvent}
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => {
              onAddEvent(cell, suggestion);
              setAnchorEl(null);
            }}
            sx={{ textTransform: "none" }}
          >
            {hasSuggestion
              ? t("rotationGrid.addSuggested")
              : t("rotationGrid.addEvent")}
          </Button>
        </Popover>
      )}
    </>
  );
}

// ─── Review Panel ────────────────────────────────────────────────────────────

const dateFmt: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" };

function ReviewPanel({
  analysis,
  grid,
  onAccept,
  onDeleteReading,
  onResolved,
}: {
  analysis: RotationAnalysis | null;
  grid: GridCell[][];
  onAccept: (cell: GridCell) => void;
  onDeleteReading: (readingGuid: string, fieldGuid: string) => void;
  onResolved: () => void;
}) {
  const { t } = useTranslation();
  const [dismissedProposals, setDismissedProposals] = useState<Set<string>>(
    new Set()
  );
  const [acceptedProposals, setAcceptedProposals] = useState<Set<string>>(
    new Set()
  );
  const [celebrating, setCelebrating] = useState(false);
  const [expanded, setExpanded] = useState(true);

  // Find proposed cells from the grid
  const proposedCells = useMemo(() => {
    const cells: GridCell[] = [];
    for (const row of grid) {
      for (const cell of row) {
        if (cell.status === "proposed" && cell.origin === "machine") {
          cells.push(cell);
        }
      }
    }
    return cells;
  }, [grid]);

  const visibleProposals = proposedCells.filter(
    (c) =>
      !dismissedProposals.has(c.moveEvent?.dateTimeIn ?? "") &&
      !acceptedProposals.has(c.moveEvent?.dateTimeIn ?? "")
  );

  const totalItems = proposedCells.length;
  const completedItems = acceptedProposals.size + dismissedProposals.size;
  const progress = totalItems > 0 ? completedItems / totalItems : 0;
  const allDone = totalItems > 0 && completedItems >= totalItems;

  // Trigger celebration when all done
  useEffect(() => {
    if (allDone && !celebrating) {
      setCelebrating(true);
      onResolved();
      const timer = setTimeout(() => setCelebrating(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [allDone, celebrating, onResolved]);

  if (!analysis) return null;
  if (totalItems === 0) return null;

  if (celebrating) {
    return (
      <Box
        sx={{
          mb: 2,
          px: 2,
          py: 2.5,
          borderRadius: 2,
          backgroundColor: COLORS.dahlia[50],
          border: `1px solid ${COLORS.dahlia[300]}`,
          textAlign: "center",
          animation: "celebrateIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)",
          "@keyframes celebrateIn": {
            "0%": { transform: "scale(0.8)", opacity: 0 },
            "60%": { transform: "scale(1.04)" },
            "100%": { transform: "scale(1)", opacity: 1 },
          },
        }}
      >
        <Typography
          sx={{
            fontSize: "1.5rem",
            mb: 0.5,
            animation: "popIn 0.4s ease-out 0.2s both",
            "@keyframes popIn": {
              "0%": { transform: "scale(0)", opacity: 0 },
              "100%": { transform: "scale(1)", opacity: 1 },
            },
          }}
        >
          🎉
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: COLORS.dahlia[900],
            fontSize: "0.95rem",
          }}
        >
          {t("review.allDone")}
        </Typography>
        <Typography variant="caption" sx={{ color: COLORS.dahlia[700] }}>
          {t("review.allDoneDetail")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mb: 2,
        borderRadius: 2,
        border: `1px solid ${COLORS.bellflower[300]}`,
        backgroundColor: COLORS.bellflower[50],
        overflow: "hidden",
      }}
    >
      {/* Header — clickable to expand/collapse */}
      <Box
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          px: 2,
          py: 1.25,
          backgroundColor: COLORS.bellflower[100],
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          userSelect: "none",
          "&:hover": { backgroundColor: COLORS.bellflower[300] + "40" },
          transition: "background-color 0.15s",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: COLORS.bellflower[700],
              transition: "transform 0.2s",
              transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
            }}
          >
            ▶
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: COLORS.bellflower[900] }}
          >
            {t("review.title")}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: COLORS.bellflower[700],
              backgroundColor: COLORS.bellflower[300],
              borderRadius: 10,
              px: 1,
              py: 0.1,
              fontWeight: 600,
              fontSize: "0.65rem",
            }}
          >
            {totalItems - completedItems}
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ color: COLORS.bellflower[700] }}>
          {completedItems}/{totalItems}
        </Typography>
      </Box>

      {/* Progress bar */}
      <Box
        sx={{
          height: 3,
          backgroundColor: COLORS.bellflower[100],
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${progress * 100}%`,
            backgroundColor: COLORS.dahlia[500],
            transition: "width 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
            borderRadius: "0 2px 2px 0",
          }}
        />
      </Box>

      {/* Collapsible content */}
      <Box
        sx={{
          maxHeight: expanded ? 600 : 0,
          overflow: "hidden",
          transition: "max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {/* Proposed events section */}
          {visibleProposals.map((cell, i) => {
            const key = cell.moveEvent?.dateTimeIn ?? String(i);
            return (
              <Box
                key={key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1,
                  borderRadius: 1.5,
                  backgroundColor: "white",
                  border: `1px solid ${COLORS.bellflower[300]}`,
                  animation: "slideIn 0.3s ease-out",
                  "@keyframes slideIn": {
                    "0%": { opacity: 0, transform: "translateX(-8px)" },
                    "100%": { opacity: 1, transform: "translateX(0)" },
                  },
                }}
              >
                <SatelliteAltIcon
                  sx={{
                    fontSize: 16,
                    color: COLORS.bellflower[500],
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: COLORS.bellflower[900],
                      fontSize: "0.8rem",
                    }}
                  >
                    {cell.fieldName}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: COLORS.bellflower[700], fontSize: "0.7rem" }}
                  >
                    {cell.dateIn &&
                      new Date(cell.dateIn).toLocaleDateString(
                        undefined,
                        dateFmt
                      )}
                    {cell.dateOut &&
                      ` — ${new Date(cell.dateOut).toLocaleDateString(
                        undefined,
                        dateFmt
                      )}`}
                    {!cell.dateOut && ` — ${t("rotationGrid.ongoing")}`}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => {
                      onAccept(cell);
                      setAcceptedProposals((prev) => new Set([...prev, key]));
                    }}
                    sx={{
                      minWidth: 0,
                      px: 1.5,
                      py: 0.25,
                      textTransform: "none",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      backgroundColor: COLORS.dahlia[700],
                      color: "white",
                      "&:hover": { backgroundColor: COLORS.dahlia[900] },
                      borderRadius: 1.5,
                    }}
                    startIcon={
                      <CheckCircleIcon sx={{ fontSize: "14px !important" }} />
                    }
                  >
                    {t("review.looksRight")}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      if (cell.reading) {
                        onDeleteReading(cell.reading.guid, cell.fieldGuid);
                      }
                      setDismissedProposals((prev) => new Set([...prev, key]));
                    }}
                    sx={{
                      minWidth: 0,
                      px: 1,
                      py: 0.25,
                      textTransform: "none",
                      fontSize: "0.75rem",
                      borderColor: COLORS.bellflower[300],
                      color: COLORS.bellflower[700],
                      "&:hover": {
                        borderColor: COLORS.bellflower[500],
                        backgroundColor: COLORS.bellflower[100],
                      },
                      borderRadius: 1.5,
                    }}
                  >
                    {t("review.notRight")}
                  </Button>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
      {/* end collapsible content */}
    </Box>
  );
}

// ─── Add Missing Event Picker ────────────────────────────────────────────────

function AddMissingEventPicker({
  missingFields,
  lastEventEnd,
  onAdd,
}: {
  missingFields: MissingField[];
  lastEventEnd: string | null;
  onAdd: (fieldGuid: string, dateIn: string, dateOut: string | null) => void;
}) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedField, setSelectedField] = useState<MissingField | null>(null);
  const [dateIn, setDateIn] = useState("");
  const [dateOut, setDateOut] = useState("");

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setSelectedField(null);
    // Default dateIn to day after last event
    if (lastEventEnd) {
      const next = new Date(new Date(lastEventEnd).getTime() + 86400000);
      setDateIn(next.toISOString().slice(0, 10));
    } else {
      setDateIn("");
    }
    setDateOut("");
  };

  const handleSubmit = () => {
    if (!selectedField || !dateIn) return;
    const din = new Date(dateIn + "T12:00:00Z").toISOString();
    const dout = dateOut
      ? new Date(dateOut + "T12:00:00Z").toISOString()
      : null;
    onAdd(selectedField.guid, din, dout);
    setAnchorEl(null);
  };

  return (
    <>
      <Box
        sx={{
          mt: 0.5,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        {/* Add button */}
        <Button
          size="small"
          variant="outlined"
          startIcon={
            <AddCircleOutlineIcon sx={{ fontSize: "14px !important" }} />
          }
          onClick={handleOpen}
          sx={{
            textTransform: "none",
            fontSize: "0.65rem",
            fontWeight: 500,
            color: COLORS.bellflower[700],
            borderColor: COLORS.bellflower[300],
            borderStyle: "dashed",
            borderRadius: 1.5,
            py: 0.5,
            "&:hover": {
              borderColor: COLORS.bellflower[500],
              backgroundColor: COLORS.bellflower[50],
              borderStyle: "dashed",
            },
          }}
        >
          {t("rotationGrid.addEvent")}
        </Button>
      </Box>

      {/* Field picker popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{
          paper: {
            sx: { borderRadius: 2, p: 2, minWidth: 220, maxWidth: 280 },
          },
        }}
      >
        {!selectedField ? (
          <>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 1, fontSize: "0.8rem" }}
            >
              {t("review.pickField")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              {missingFields.map((f) => (
                <Button
                  key={f.guid}
                  size="small"
                  variant="outlined"
                  onClick={() => setSelectedField(f)}
                  sx={{
                    textTransform: "none",
                    justifyContent: "flex-start",
                    fontWeight: 500,
                    fontSize: "0.8rem",
                    borderColor: COLORS.dahlia[300],
                    color: COLORS.dahlia[900],
                    "&:hover": {
                      backgroundColor: COLORS.dahlia[50],
                      borderColor: COLORS.dahlia[500],
                    },
                    borderRadius: 1.5,
                  }}
                >
                  {f.name}
                </Button>
              ))}
            </Box>
          </>
        ) : (
          <>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 0.5, fontSize: "0.8rem" }}
            >
              {selectedField.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block", mb: 1.5 }}
            >
              {t("review.enterDates")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <TextField
                label={t("rotationGrid.editDateIn")}
                type="date"
                size="small"
                value={dateIn}
                onChange={(e) => setDateIn(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
              />
              <TextField
                label={t("rotationGrid.editDateOut")}
                type="date"
                size="small"
                value={dateOut}
                onChange={(e) => setDateOut(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  disabled={!dateIn}
                  onClick={handleSubmit}
                  sx={{
                    flex: 1,
                    textTransform: "none",
                    backgroundColor: COLORS.dahlia[700],
                    "&:hover": { backgroundColor: COLORS.dahlia[900] },
                  }}
                >
                  {t("edit.button.save")}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setSelectedField(null)}
                  sx={{ textTransform: "none" }}
                >
                  {t("edit.button.back")}
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Popover>
    </>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function RotationSeasonGrid({
  animalGroupId,
  animalGroup,
  stockClasses,
}: {
  animalGroupId: string;
  animalGroup?: AnimalGroup;
  stockClasses?: StockClass[];
}) {
  const { t } = useTranslation();
  const { data: moveEvents, isLoading: movesLoading } =
    useAnimalGroupMoveEvents(animalGroupId);
  const { data: allFields = [], isLoading: fieldsLoading } = useFields();

  const assignedFields = useMemo(
    () => getAllFieldsForAnimalGroupIdFromArray(allFields, animalGroupId),
    [allFields, animalGroupId]
  );

  // Build field list: assigned fields + historical fields from moveEvents
  // (fields may no longer be assigned but had grazing events in past seasons)
  const unsortedFields = useMemo(() => {
    const fieldByGuid = new Map<string, Field>();
    for (const f of assignedFields) {
      fieldByGuid.set(f.guid, f);
    }
    for (const ev of moveEvents ?? []) {
      if (ev.fieldGuid && !fieldByGuid.has(ev.fieldGuid)) {
        const existing = allFields.find((f) => f.guid === ev.fieldGuid);
        if (existing) {
          fieldByGuid.set(ev.fieldGuid, existing);
        } else if (ev.fieldName) {
          fieldByGuid.set(ev.fieldGuid, {
            guid: ev.fieldGuid,
            name: ev.fieldName,
          } as Field);
        }
      }
    }
    return Array.from(fieldByGuid.values());
  }, [assignedFields, moveEvents, allFields]);

  const unsortedFieldGuids = useMemo(
    () => unsortedFields.map((f) => f.guid),
    [unsortedFields]
  );
  const readingsByField = useEventsForFields(
    unsortedFieldGuids,
    "user,machine"
  );

  const seasonYears = useMemo(
    () => getSeasonYears(moveEvents ?? [], readingsByField),
    [moveEvents, readingsByField]
  );
  const [selectedYear, setSelectedYear] = useState<number>(() =>
    new Date().getFullYear()
  );

  const currentYear = new Date().getFullYear();
  const hasDataForYear = seasonYears.includes(selectedYear);

  // Filter to fields relevant to this season: assigned fields + fields with events this year
  const seasonFields = useMemo(() => {
    const assignedGuids = new Set(assignedFields.map((f) => f.guid));
    const seasonEventGuids = new Set(
      (moveEvents ?? [])
        .filter(
          (ev) =>
            ev.fieldGuid &&
            new Date(ev.dateTimeIn).getFullYear() === selectedYear
        )
        .map((ev) => ev.fieldGuid as string)
    );
    return unsortedFields.filter(
      (f) => assignedGuids.has(f.guid) || seasonEventGuids.has(f.guid)
    );
  }, [unsortedFields, assignedFields, moveEvents, selectedYear]);

  // Sort fields by rotation order for the selected season
  const fields = useMemo(
    () =>
      sortFieldsByRotationOrder(
        seasonFields,
        moveEvents ?? [],
        readingsByField,
        selectedYear
      ),
    [seasonFields, moveEvents, readingsByField, selectedYear]
  );

  const { grid, rotationCount } = useMemo(
    () => buildGrid(fields, moveEvents ?? [], readingsByField, selectedYear),
    [fields, moveEvents, readingsByField, selectedYear]
  );

  const rotationColumns = useMemo(
    () =>
      buildRotationColumns(
        assignedFields,
        moveEvents ?? [],
        readingsByField,
        selectedYear
      ),
    [assignedFields, moveEvents, readingsByField, selectedYear]
  );

  // Season date range
  const {
    seasonStart,
    seasonEnd,
    isCurrentSeason,
    warnings: seasonWarnings,
  } = useMemo(
    () =>
      getSeasonDateRange(moveEvents ?? [], readingsByField, selectedYear, t),
    [moveEvents, readingsByField, selectedYear, t]
  );

  // Rotation totals (days per rotation column)
  const rotationTotals = useMemo(() => {
    const totals: number[] = [];
    for (let rot = 0; rot < rotationCount; rot++) {
      let total = 0;
      for (const row of grid) {
        const cell = row[rot];
        if (cell?.dateIn && cell?.dateOut) {
          total += Math.max(
            0,
            (new Date(cell.dateOut).getTime() -
              new Date(cell.dateIn).getTime()) /
              86400000
          );
        }
      }
      totals.push(Math.round(total * 10) / 10);
    }
    return totals;
  }, [grid, rotationCount]);

  // Rotation analysis
  const dailyDemand = useMemo(
    () => (stockClasses ? getDemandFromArray(stockClasses) : 0),
    [stockClasses]
  );
  const postGrazingTarget = animalGroup?.postGrazingTarget ?? 0;

  const analysis: RotationAnalysis | null = useMemo(() => {
    if (!fields.length || !(moveEvents ?? []).length) return null;
    return analyzeRotation(
      fields,
      moveEvents ?? [],
      postGrazingTarget,
      dailyDemand,
      selectedYear
    );
  }, [fields, moveEvents, postGrazingTarget, dailyDemand, selectedYear]);

  // Build lookup maps for UI
  const outlierKeys = useMemo(() => {
    const set = new Set<string>();
    if (!analysis) return set;
    for (const ev of analysis.likelyPath.outliers) {
      if (ev.fieldGuid) set.add(`${ev.fieldGuid}:${ev.dateTimeIn}`);
    }
    return set;
  }, [analysis]);

  const estimateByField = useMemo(() => {
    const map: Record<string, number> = {};
    if (!analysis) return map;
    for (const est of analysis.fieldEstimates) {
      map[est.fieldGuid] = est.estimatedDays;
    }
    return map;
  }, [analysis]);

  const queryClient = useQueryClient();
  const updateReading = useUpdateReading();
  const createReading = useCreateReading();
  const deleteReading = useDeleteReading();

  const invalidateMoveEvents = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.animalGroups.moveEvents(animalGroupId),
    });
  }, [queryClient, animalGroupId]);

  const handleAccept = useCallback(
    (cell: GridCell) => {
      if (!cell.reading) return;
      updateReading.mutate(
        {
          ...cell.reading,
          manualDryMatterInput: {
            ...cell.reading.manualDryMatterInput,
            verificationStatus: "Accepted",
          },
        },
        { onSuccess: () => invalidateMoveEvents() }
      );
    },
    [updateReading, invalidateMoveEvents]
  );

  const handleSaveEditInline = useCallback(
    (cell: GridCell, dateInStr: string, dateOutStr: string) => {
      const dateIn = dateInStr ? new Date(dateInStr + "T12:00:00Z") : null;
      const dateOut = dateOutStr ? new Date(dateOutStr + "T12:00:00Z") : null;
      if (!dateIn) return;

      if (cell.reading) {
        updateReading.mutate(
          {
            ...cell.reading,
            manualDryMatterInput: {
              ...cell.reading.manualDryMatterInput,
              dateTimeMeasurementStart: dateIn.toISOString(),
              dateTimeMeasurementEnd: (dateOut ?? dateIn).toISOString(),
              event: {
                ...cell.reading.manualDryMatterInput.event,
                dateTimeIn: dateIn.toISOString(),
                dateTimeOut: dateOut?.toISOString(),
              },
            },
          },
          { onSuccess: () => invalidateMoveEvents() }
        );
      } else {
        createReading.mutate(
          {
            guid: "new",
            manualDryMatterInput: {
              field: cell.fieldGuid,
              dateTimeMeasurementStart: dateIn.toISOString(),
              dateTimeMeasurementEnd: (dateOut ?? dateIn).toISOString(),
              event: {
                eventType: EventType.GrazingEvent,
                dateTimeIn: dateIn.toISOString(),
                dateTimeOut: dateOut?.toISOString(),
              },
              kind: "user",
              verificationStatus: "Accepted",
            },
          },
          { onSuccess: () => invalidateMoveEvents() }
        );
      }
    },
    [updateReading, createReading, invalidateMoveEvents]
  );

  const handleFixEndDate = useCallback(
    (cell: GridCell, suggestedEnd: Date) => {
      if (cell.reading) {
        // Update the existing reading with the suggested end date
        updateReading.mutate(
          {
            ...cell.reading,
            manualDryMatterInput: {
              ...cell.reading.manualDryMatterInput,
              dateTimeMeasurementEnd: suggestedEnd.toISOString(),
              event: {
                ...cell.reading.manualDryMatterInput.event,
                dateTimeOut: suggestedEnd.toISOString(),
              },
            },
          },
          { onSuccess: () => invalidateMoveEvents() }
        );
      } else if (cell.dateIn) {
        // No reading exists (moveEvent only) — create one with the end date
        const newReading: ManualDMInputResponse = {
          guid: "new",
          manualDryMatterInput: {
            field: cell.fieldGuid,
            dateTimeMeasurementStart: cell.dateIn,
            dateTimeMeasurementEnd: suggestedEnd.toISOString(),
            event: {
              eventType: EventType.GrazingEvent,
              dateTimeIn: cell.dateIn,
              dateTimeOut: suggestedEnd.toISOString(),
            },
            kind: "user",
            verificationStatus: "Accepted",
          },
        };
        createReading.mutate(newReading, {
          onSuccess: () => invalidateMoveEvents(),
        });
      }
    },
    [updateReading, createReading, invalidateMoveEvents]
  );

  const handleDeleteReading = useCallback(
    (readingGuid: string, fieldGuid: string) => {
      deleteReading.mutate(
        { readingId: readingGuid, fieldId: fieldGuid },
        { onSuccess: () => invalidateMoveEvents() }
      );
    },
    [deleteReading, invalidateMoveEvents]
  );

  const handleAddEvent = useCallback(
    (
      cell: GridCell,
      suggestion: { suggestedIn: Date | null; suggestedOut: Date | null }
    ) => {
      const dateIn = suggestion.suggestedIn ?? new Date();
      const dateOut = suggestion.suggestedOut ?? undefined;

      const newReading: ManualDMInputResponse = {
        guid: "new",
        manualDryMatterInput: {
          field: cell.fieldGuid,
          dateTimeMeasurementStart: dateIn.toISOString(),
          dateTimeMeasurementEnd: (dateOut ?? dateIn).toISOString(),
          event: {
            eventType: EventType.GrazingEvent,
            dateTimeIn: dateIn.toISOString(),
            dateTimeOut: dateOut?.toISOString(),
          },
          kind: "user",
          verificationStatus: "Accepted",
        },
      };

      createReading.mutate(newReading, {
        onSuccess: () => invalidateMoveEvents(),
      });
    },
    [createReading, invalidateMoveEvents]
  );

  if (movesLoading || fieldsLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!moveEvents || moveEvents.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {t("rotationTimeline.empty")}
        </Typography>
      </Box>
    );
  }

  const seasonDateFmt: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
  };

  return (
    <Box>
      {/* Year navigator — centered */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            size="small"
            onClick={() => setSelectedYear((y) => y - 1)}
            sx={{ minWidth: 0, p: 0.5, color: COLORS.bellflower[500] }}
          >
            <ChevronLeftIcon sx={{ fontSize: 24 }} />
          </Button>
          <Typography
            sx={{
              fontWeight: 800,
              color: COLORS.dahlia[900],
              fontSize: "1.4rem",
              minWidth: 60,
              textAlign: "center",
              letterSpacing: "0.02em",
            }}
          >
            {selectedYear}
          </Typography>
          <Button
            size="small"
            onClick={() => setSelectedYear((y) => y + 1)}
            disabled={selectedYear >= currentYear}
            sx={{ minWidth: 0, p: 0.5, color: COLORS.bellflower[500] }}
          >
            <ChevronRightIcon sx={{ fontSize: 24 }} />
          </Button>
        </Box>

        {/* Season date range — below year */}
        {seasonStart && (
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", mt: 0.25 }}
          >
            {seasonStart.toLocaleDateString(undefined, seasonDateFmt)}
            {" — "}
            {seasonEnd
              ? seasonEnd.toLocaleDateString(undefined, seasonDateFmt)
              : t("rotationGrid.ongoing")}
            {seasonEnd &&
              ` · ${getDaysBetween(
                seasonStart.toISOString(),
                seasonEnd.toISOString()
              )} ${t("rotationGrid.days")}`}
          </Typography>
        )}
      </Box>

      {/* Empty state for years with no data */}
      {rotationColumns.length === 0 && (
        <Box
          sx={{
            py: 6,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            color: "text.secondary",
          }}
        >
          <Typography
            variant="body1"
            sx={{ fontWeight: 500, mb: 0.5, color: COLORS.bellflower[700] }}
          >
            {t("rotationGrid.emptyYear", { year: selectedYear })}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.disabled",
              maxWidth: 320,
              textAlign: "center",
              mb: 2,
            }}
          >
            {t("rotationGrid.emptyYearHint")}
          </Typography>
          <AddMissingEventPicker
            missingFields={assignedFields.map((f) => ({
              guid: f.guid,
              name: f.name,
            }))}
            lastEventEnd={null}
            onAdd={(fieldGuid, dateIn, dateOut) => {
              const newReading: ManualDMInputResponse = {
                guid: "new",
                manualDryMatterInput: {
                  field: fieldGuid,
                  dateTimeMeasurementStart: dateIn,
                  dateTimeMeasurementEnd: dateOut ?? dateIn,
                  event: {
                    eventType: EventType.GrazingEvent,
                    dateTimeIn: dateIn,
                    dateTimeOut: dateOut ?? undefined,
                  },
                  kind: "user",
                  verificationStatus: "Accepted",
                },
              };
              createReading.mutate(newReading, {
                onSuccess: () => invalidateMoveEvents(),
              });
            }}
          />
        </Box>
      )}

      {/* Rotation columns */}
      {rotationColumns.length > 0 && (
        <Box sx={{ overflowX: "auto", pb: 1, mx: -2, px: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${rotationColumns.length}, minmax(160px, 260px))`,
              gap: 2,
              alignItems: "start",
              width: "fit-content",
              mx: "auto",
            }}
          >
            {rotationColumns.map((col) => (
              <Box key={col.rotation}>
                {/* Column header */}
                <Box sx={{ textAlign: "center", py: 0.75, mb: 0.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: COLORS.bellflower[700],
                      fontSize: "0.7rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      lineHeight: 1.2,
                    }}
                  >
                    {t("rotationGrid.rotation", { number: col.rotation })}
                  </Typography>
                  {col.totalDays > 0 && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.disabled",
                        fontSize: "0.6rem",
                        display: "block",
                        lineHeight: 1.2,
                      }}
                    >
                      {col.totalDays}d total
                    </Typography>
                  )}
                </Box>

                {/* Stacked event cards */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  {col.events.map((cell, ci) => (
                    <GrazingEventCell
                      key={`${cell.fieldGuid}-${cell.dateIn}-${ci}`}
                      cell={cell}
                      onAccept={handleAccept}
                      onSaveEdit={handleSaveEditInline}
                      onAddEvent={handleAddEvent}
                      onDeleteReading={handleDeleteReading}
                      isAddingEvent={createReading.isPending}
                      isSaving={
                        updateReading.isPending || createReading.isPending
                      }
                      grid={grid}
                      fieldIndex={0}
                      rotationIndex={ci}
                    />
                  ))}

                  {/* Missing fields — add event picker */}
                  {col.missingFields.length > 0 && col.events.length >= 2 && (
                    <AddMissingEventPicker
                      missingFields={col.missingFields}
                      lastEventEnd={
                        col.events[col.events.length - 1]?.dateOut ??
                        col.events[col.events.length - 1]?.dateIn ??
                        null
                      }
                      onAdd={(fieldGuid, dateIn, dateOut) => {
                        const newReading: ManualDMInputResponse = {
                          guid: "new",
                          manualDryMatterInput: {
                            field: fieldGuid,
                            dateTimeMeasurementStart: dateIn,
                            dateTimeMeasurementEnd: dateOut ?? dateIn,
                            event: {
                              eventType: EventType.GrazingEvent,
                              dateTimeIn: dateIn,
                              dateTimeOut: dateOut ?? undefined,
                            },
                            kind: "user",
                            verificationStatus: "Accepted",
                          },
                        };
                        createReading.mutate(newReading, {
                          onSuccess: () => invalidateMoveEvents(),
                        });
                      }}
                    />
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
